import * as graphql from 'graphql.js';
import { encode } from 'base-64';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { ItemTypes } from '../internal/constants/ItemTypes';

import { sanitizeJsonForQuery } from '~utils/ContentFormatter';
import { createItemParamsFromIds } from './utils/CreateItemParamsFromIds';
import { IFeatures } from '~constants/Features';
import { IQueryTypeFragments, QueryFragments } from './QueryFragments';
import { ProductQueryStashingService } from './ProductQueryStashingService';
import { QueryType } from './features/Default';
import { buildProductQueryFilter } from './utils/BuildProductQueryFilter';
import { buildQueryFilters, buildQuerySorters, buildSelectionSet } from './utils/BuildQueryParts';
import { IItem } from './interfaces/IItem';
import { ICoreProduct } from './interfaces/IProduct';
import { IParamsById } from './interfaces/IParamsById';
import { IApiCallParams } from './interfaces/IParams';
import { IProductDataOptions } from './interfaces/IProductDataOptions';
import { IIncludesQuery, ISkuFilterQuery } from './interfaces/ISkuFilterQuery';

interface IProdcatApiSdk {
    baseUrl: string;
    businessUnit: string;
    clientId: string;
    productDisplayEnvs: IEnvironments;
}

enum TagFilterType {
    id = 'id',
    key = 'key'
}

enum Environments {
    dev = 'dev',
    stage = 'stage',
    prod = 'prod'
}

interface ITagFilterQuery {
    id?: IIncludesQuery;
    key?: IIncludesQuery;
}

interface ITagsFilterQuery {
    filter: ITagFilterQuery;
    includeInnerHits: boolean;
}

interface ITagsFilter {
    tags: ITagsFilterQuery;
    skus?: ISkuFilterQuery;
}

export type IEnvironments = {
    [key in Environments]: boolean;
};

interface IApiConfig {
    graphqlExtensionVersion?: string;
    clientId?: string;
    baseUrl?: string;
    businessUnitId?: string;
    features?: IFeatures;
    productDisplayEnvs: IEnvironments;
}

const PRODUCT_QUERY_DEBOUNCE_VALUE = 500;

export class ProdcatApiSdk {
    // todo: dynamically derive the appropriate versions from the config service
    private configs: IApiConfig;
    private productQueryStashingService: ProductQueryStashingService;
    // we should be able to remove this as it's gonna be per controller instance
    private queryFragments: QueryFragments;
    public coreVersion: string = 'v1';
    private apiVersion: string = 'v3';
    private queries = new Map();
    private readonly logger: ELCLogger;

    constructor(config: IProdcatApiSdk, logger: ELCLogger) {
        this.logger = logger;

        const { baseUrl, businessUnit, clientId, productDisplayEnvs } = config;

        this.configs = {
            baseUrl,
            businessUnitId: businessUnit,
            clientId,
            productDisplayEnvs
        };
        this.productQueryStashingService = new ProductQueryStashingService();
    }

    public setQueryFragments(queryFragments: QueryFragments) {
        this.queryFragments = queryFragments;
    }

    public setConfigs(config: Partial<IApiConfig>) {
        this.configs = { ...this.configs, ...config };
    }

    private get prodcatUrl() {
        return `stardust-prodcat-product-${this.apiVersion}/graphql/core/${this.coreVersion}/extension/${this.configs.graphqlExtensionVersion}`;
    }

    private get composedUrl() {
        return `${this.configs.baseUrl}/${this.prodcatUrl}`;
    }

    private get headers() {
        return {
            'business-unit': this.configs.businessUnitId,
            'Cache-Control': 'no-cache',
            ClientId: this.configs.clientId,
            authorizationtoken: encode(`${this.configs.businessUnitId}:${window.location.origin}`)
        };
    }

    private get productDisplayEnvsFormatted(): string {
        return JSON.stringify(this.configs.productDisplayEnvs).replace(/['"]+/g, '');
    }

    private get buildEnvironmentArgument(): string {
        return this.configs.productDisplayEnvs
            ? `environment: ${this.productDisplayEnvsFormatted},`
            : '';
    }

    private get client() {
        return graphql(this.composedUrl, {
            headers: this.headers,
            fragments: {
                ...this.queryFragments.enabledFragments
            },
            asJSON: true
        });
    }

    private clientQuery =
        (query: string, useQueryCache: boolean = true) =>
        async () => {
            if (useQueryCache && this.queries.has(query)) {
                return this.queries.get(query);
            }

            const queryPromise = new Promise((resolve) => {
                resolve(this.client(query)());
            });

            this.queries.set(query, queryPromise);

            return queryPromise;
        };

    private getDebouncedProducts = async () => {
        const { items, queue } = this.productQueryStashingService.prepareProductsQuery();
        const products = await this.getProductsById(items);

        this.productQueryStashingService.resolveQueue(products, queue);
    };

    private buildProductQueryFilterByTags = (
        tags: string[],
        filterById: boolean = false
    ): string => {
        const productsFilter: ITagsFilter = {
            tags: {
                filter: {
                    [filterById ? TagFilterType.id : TagFilterType.key]: {
                        in: tags
                    }
                },
                includeInnerHits: true
            }
        };

        return sanitizeJsonForQuery(JSON.stringify(productsFilter));
    };

    public getProductData = async (
        options: IProductDataOptions,
        queryFragments: IQueryTypeFragments = this.queryFragments.queryFragments,
        useQueryCache: boolean = true
    ) => {
        const { filterBy, sortBy = {}, query = '' } = options;

        try {
            const response = await this.clientQuery(
                `{
                products(${this.buildEnvironmentArgument}
                    filter: ${buildQueryFilters(filterBy)},
                    sort: ${buildQuerySorters(sortBy)}
                ) {
                    ${buildSelectionSet(query, queryFragments)}
                }
            }`,
                useQueryCache
            )();

            const data = await Promise.resolve(response.products.items);

            if (!data || data.length === 0) {
                throw new Error('Response object empty');
            }

            return data;
        } catch (errors) {
            this.logger.error({
                triggerType: TriggerType.graphqlApi,
                message: `Error loading products, ${JSON.stringify(filterBy)}`,
                payload: {
                    error: Array.isArray(errors) ? errors[0] : errors
                }
            });

            return [];
        }
    };

    public getProductsById = async (
        items: IItem[],
        params: IApiCallParams = {
            includeInnerHits: true
        },
        useQueryCache: boolean = true
    ): Promise<ICoreProduct[]> => {
        try {
            if (items.length === 0) {
                throw new Error('No items provided for product query');
            }

            const { includeInnerHits, queryFragments = this.queryFragments.queryFragments } =
                params;
            // skus total
            const response = await this.clientQuery(
                `
                {
                    products(
                    ${this.buildEnvironmentArgument}
                    filter: ${buildProductQueryFilter(items, includeInnerHits)}) {
                        items {
                            ${queryFragments[QueryType.product]}
                            skus {
                                total
                                items {
                                    ${queryFragments[QueryType.skus]}
                                }
                            }
                        }
                    }
                }
            `,
                useQueryCache
            )();

            return Promise.resolve(response.products.items);
        } catch (errors) {
            this.logger.error({
                triggerType: TriggerType.graphqlApi,
                message: `Error loading products by id: ${JSON.stringify(items)}`,
                payload: {
                    error: Array.isArray(errors) ? errors[0] : errors
                }
            });

            return [];
        }
    };

    public getProductById = async (
        productId: string,
        useDebounce?: boolean,
        queryFragments: IQueryTypeFragments = this.queryFragments.queryFragments
    ): Promise<ICoreProduct> => {
        if (useDebounce) {
            this.productQueryStashingService.debounce(
                this.getDebouncedProducts,
                PRODUCT_QUERY_DEBOUNCE_VALUE
            );

            return this.productQueryStashingService.createQueryPromise(productId);
        }

        const productItem = createItemParamsFromIds([productId], ItemTypes.product);
        const products = await this.getProductsById(productItem, { queryFragments });
        const [product] = products || [];

        return product;
    };

    public getProductsByTag = async (
        productTags: string[],
        filterById?: boolean,
        queryFragments: IQueryTypeFragments = this.queryFragments.queryFragments,
        useQueryCache: boolean = true
    ): Promise<ICoreProduct[]> => {
        try {
            const response = await this.clientQuery(
                `
            {
                products(
                    ${this.buildEnvironmentArgument}
                    filter: ${this.buildProductQueryFilterByTags(productTags, filterById)}, sort: [
                    { tags: { product_display_order: ASCENDING } },
                ]) {
                    items {
                        ${queryFragments[QueryType.product]}
                        skus {
                            total
                            items {
                                ${queryFragments[QueryType.skus]}
                            }
                        }
                    }
                }
            }
        `,
                useQueryCache
            )();

            return Promise.resolve(response.products.items);
        } catch (errors) {
            this.logger.error({
                triggerType: TriggerType.graphqlApi,
                message: `Error loading products by tags ${JSON.stringify(productTags)}`,
                payload: {
                    error: errors[0]
                }
            });

            return [];
        }
    };

    /* only used by ProductSkuRepository */
    public getProducts = async (
        paramsById: IParamsById,
        queryFragment: string = '',
        useQueryCache: boolean = true
    ): Promise<ICoreProduct[]> => {
        const productIds = paramsById.productIds || [];
        const skuIds = paramsById.skuIds || [];
        const categoryIds = paramsById.categoryIds || [];

        const finalProductIds = JSON.stringify(productIds);
        const finalSkuIds = JSON.stringify(skuIds);
        const finalCategoryIds = JSON.stringify(categoryIds);
        try {
            const response = await this.clientQuery(
                `
            {
                products(
                    ${this.buildEnvironmentArgument}
                    filter: [
                        { product_id: { in: ${finalProductIds} } },
                        { tags: { filter: { id: { in: ${finalCategoryIds} } } } },
                        { skus: { filter: { sku_id: { in: ${finalSkuIds} } } } }
                    ]) {
                        items {
                            ${queryFragment}
                            skus {
                                items {
                                    sku_id
                                    sku_badge
                                }
                            }
                        }
                    }
            }`,
                useQueryCache
            )();

            return Promise.resolve(response.products.items);
        } catch (errors) {
            this.logger.error({
                triggerType: TriggerType.graphqlApi,
                message: `Error at getProducts:
${JSON.stringify(paramsById)}
`,
                payload: {
                    error: errors[0]
                }
            });

            return [];
        }
    };
}
